import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSearchModal } from '@/components/shared/SearchModal/useSearchModal';
import { toast } from '@/components/ui/toast';
import { ROUTES, routeTo } from '@/config/routes';
import { BrandAssetService } from '@/services/brand-asset.service';

/** The modal's four steps, and how far along the bar is when each one begins. */
const STEP_PROGRESS = [10, 35, 65, 90] as const;
const STEP_LABELS = [
  'Validating the URL…',
  'Scanning the website…',
  'Recognising the brand…',
  'Preparing the report…',
] as const;

/** How long each step is shown before the next, while the server is still working. */
const STEP_MS = 6_000;

/**
 * The slug the overview's address carries: the site's name without its scheme, `www.` or TLD,
 * so `https://www.stratapps.com/` is `stratapps`. The overview looks the brand up by it, so
 * it has to match what the Brand Analyser list builds from the same site.
 */
function slugOf(site: string): string {
  return (
    site
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split(/[/?#]/)[0]
      ?.split('.')[0]
      ?.replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') ?? ''
  );
}

/**
 * Runs the Forward analysis for a domain someone typed in the top bar.
 *
 * <p>The server answers once, at the end, so the modal's progress is an estimate that walks
 * the first three steps on a timer and stops at 90% until the answer arrives. It never claims
 * more than the server has done.
 *
 * <p>On success it opens that brand's overview. The analysis carries no brand id, so the
 * overview finds the brand by its slug, as it does when the address is typed.
 */
export function useAnalyseDomain() {
  const navigate = useNavigate();
  const modal = useSearchModal();
  const [isAnalysing, setIsAnalysing] = useState(false);
  const busy = useRef(false);

  const analyse = useCallback(
    async (input: string): Promise<boolean> => {
      const domain = input.trim();
      if (!domain || busy.current) return false;

      busy.current = true;
      setIsAnalysing(true);
      modal.startBrandAnalysis({ url: domain });

      let step = 0;
      const advance = () => {
        modal.updateProgress(STEP_LABELS[step] ?? '', STEP_PROGRESS[step] ?? 0);
        modal.updateStepStatus(step, 'in-progress');
        if (step > 0) modal.updateStepStatus(step - 1, 'complete');
      };
      advance();
      const timer = window.setInterval(() => {
        if (step < STEP_PROGRESS.length - 1) {
          step += 1;
          advance();
        }
      }, STEP_MS);

      try {
        const result = await BrandAssetService.analyzeDomain(domain);
        if (!result.success) {
          toast.error(result.error || 'Could not analyse this domain.');
          return false;
        }
        modal.updateProgress('Done', 100);
        toast.success('Brand analysed');
        const site = result.data?.Company.Website || domain;
        const slug = slugOf(site) || slugOf(domain);
        void navigate(routeTo.brandOverview(slug), {
          state: {
            brandData: {
              brandName: result.data?.Company.Name,
              domain: site,
            },
            from: ROUTES.brandAnalyser,
          },
        });
        return true;
      } finally {
        window.clearInterval(timer);
        modal.hideModal();
        busy.current = false;
        setIsAnalysing(false);
      }
    },
    [modal, navigate],
  );

  return { analyse, isAnalysing };
}
